// Calendar-day arithmetic on "YYYY-MM-DD" strings (WIB business days as the API takes them). Pure: no time zone
// is involved, every value is a calendar date computed in UTC.

/** A calendar day, "2026-10-24". */
export type Day = string;
/** A calendar month, "2026-10". */
export type Month = string;

const pad = (n: number) => String(n).padStart(2, '0');
const toUtc = (d: Day) => {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day));
};
const fromUtc = (t: Date): Day => `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;

/** A real calendar day in YYYY-MM-DD form (not "2026-02-30"). */
export const isDay = (s: string | null | undefined): s is Day => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s) && fromUtc(toUtc(s)) === s;

export function addDays(d: Day, n: number): Day {
  const t = toUtc(d);
  t.setUTCDate(t.getUTCDate() + n);
  return fromUtc(t);
}

export const monthOf = (d: Day): Month => d.slice(0, 7);
export const firstOfMonth = (m: Month): Day => `${m}-01`;
export function lastOfMonth(m: Month): Day {
  const [y, mo] = m.split('-').map(Number);
  return fromUtc(new Date(Date.UTC(y, mo, 0)));
}
export function addMonths(m: Month, n: number): Month {
  const [y, mo] = m.split('-').map(Number);
  const t = new Date(Date.UTC(y, mo - 1 + n, 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}`;
}

/** The same day of the month `n` months away, kept inside that month (31 Jan + 1 month = 28/29 Feb). */
export function addMonthsToDay(d: Day, n: number): Day {
  const m = addMonths(monthOf(d), n);
  const last = lastOfMonth(m);
  const day = `${m}-${d.slice(8, 10)}`;
  return day > last ? last : day;
}

/** The same day `n` years away (29 Feb + 1 year = 28 Feb), like the backend's maxNextTestDue. */
export const addYearsToDay = (d: Day, n: number): Day => addMonthsToDay(d, n * 12);

/** 0 = Monday … 6 = Sunday (weeks start on Monday, as Indonesian calendars do). */
export const weekdayIndex = (d: Day) => (toUtc(d).getUTCDay() + 6) % 7;

/** The weeks of a month view: whole Monday–Sunday rows, days of the neighbouring months filling the edges. */
export function monthWeeks(m: Month): Day[][] {
  const first = firstOfMonth(m);
  const last = lastOfMonth(m);
  let d = addDays(first, -weekdayIndex(first));
  const weeks: Day[][] = [];
  while (d <= last) {
    const week: Day[] = [];
    for (let i = 0; i < 7; i++, d = addDays(d, 1)) week.push(d);
    weeks.push(week);
  }
  return weeks;
}

/** `d` moved inside [min, max] (either may be missing). */
export const clampDay = (d: Day, min?: Day, max?: Day): Day => (min && d < min ? min : max && d > max ? max : d);

/** The two ends in order. */
export const ordered = (a: Day, b: Day): [Day, Day] => (a <= b ? [a, b] : [b, a]);

/**
 * A date being picked part by part (DateField): "YYYY-MM-DD" once whole, else the parts chosen so far with empty
 * slots ("2026--", "-03-05"); '' when nothing is chosen. Only a whole, real day (`isDay`) is ever sent anywhere.
 */
export interface DayParts {
  y: string;
  m: string;
  d: string;
}
export function dayParts(v: string): DayParts {
  const [y = '', m = '', d = ''] = v.split('-');
  return { y, m, d };
}
export const joinDayParts = (p: DayParts): string => (p.y || p.m || p.d ? `${p.y}-${p.m}-${p.d}` : '');
/** Some parts are chosen, not all of them (or they make no real day): the field asks to complete it. */
export const isPartialDay = (v: string) => v !== '' && !isDay(v);
/** Days in a month (1-12) of a year: 28 to 31. */
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
